import { useCallback, useEffect, useState } from "react";
import type { SectionProps } from "./context";
import { Band, Head, cn, rv } from "./ui";
import { apiErrorText } from "@/lib/api";
import { useAuth } from "./auth";
import { goAnchor } from "./app-nav";
import { showCompany } from "./companies-api";
import { showContact } from "./contacts-api";
import { money } from "./deals-api";
import { DocRow } from "./finance";
import { financeApi, type CustomerFinance, type Receivable, type Receivables } from "./finance-api";
import { locale, t, tn } from "@/lib/i18n";

/* Этап 6, часть 2 — раздел «Финансы»: дебиторка по клиентам Zoho Books (только чтение, зеркало каждые 15 минут).
   Плитки итогов, старение долга, список клиентов с долгом (просрочка, срок, последняя оплата, продавец), раскрытие строки —
   документы клиента. Без права «Финансы всех клиентов» сотрудник видит только своих клиентов. */

const day = (iso: string | null | undefined) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString(locale, { day: "numeric", month: "short", year: "2-digit" }) : "—");
const days = (n: number) => tn(n, "день", "дня", "дней");
const input =
  "h-9 w-full rounded-btn border border-border bg-background px-2.5 text-[13px] text-foreground outline-none transition focus:border-primary";

const AGING: { id: keyof Receivables["aging"]; label: string; tone: string }[] = [
  { id: "current", label: t("В срок"), tone: "bg-emerald-500" },
  { id: "d1_30", label: t("1–30 дн."), tone: "bg-amber-400" },
  { id: "d31_60", label: t("31–60 дн."), tone: "bg-orange-500" },
  { id: "d61_90", label: t("61–90 дн."), tone: "bg-red-500" },
  { id: "d90plus", label: t("90+ дн."), tone: "bg-red-800" },
];

function FinanceDemo({ title, subtitle }: SectionProps) {
  const { user } = useAuth();
  const canAll = Boolean(user?.can.financeAll);
  const [q, setQ] = useState("");
  const [mine, setMine] = useState(false);
  const [onlyOverdue, setOnlyOverdue] = useState(false);
  const [seller, setSeller] = useState("");
  const [data, setData] = useState<Receivables | null>(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(() => {
    setError("");
    return financeApi.receivables({ q, mine: mine && canAll, overdue: onlyOverdue }).then(setData, (e) => setError(apiErrorText(e)));
  }, [q, mine, onlyOverdue, canAll]);
  useEffect(() => {
    const h = setTimeout(() => void load(), 150);
    return () => clearTimeout(h);
  }, [load]);

  const items = (data?.items ?? []).filter((x) => !seller || x.salesperson === seller);
  const totals = data?.totals;
  const cur = items[0]?.currency ?? "AED";
  const agingSum = data ? AGING.reduce((s, a) => s + data.aging[a.id], 0) : 0;

  const tiles = totals
    ? [
        { label: t("Долг всего"), value: money(totals.receivable, cur), sub: t("{n} открытых счетов", { n: totals.openInvoices }), tone: totals.receivable > 0.5 ? "text-amber-600" : "" },
        { label: t("Просрочено"), value: money(totals.overdue, cur), sub: totals.receivable ? t("{p}% долга", { p: Math.round((totals.overdue / totals.receivable) * 100) }) : "", tone: totals.overdue > 0.5 ? "text-red-600" : "" },
        { label: t("Клиентов с долгом"), value: String(totals.customers), sub: t("с просрочкой — {n}", { n: totals.overdueCustomers }), tone: "" },
        { label: t("На дату"), value: day(totals.today), sub: t("зеркало Books, каждые 15 минут"), tone: "" },
      ]
    : [];

  const openCard = (x: Receivable) => {
    if (x.companyId) {
      showCompany(x.companyId);
      goAnchor("#companies");
    } else if (x.contactId) {
      showContact(x.contactId);
      goAnchor("#contacts-app");
    }
  };

  return (
    <Band id="finance" tone="muted">
      <Head title={title} subtitle={subtitle} />
      <div {...rv(1)} className="mt-6 overflow-hidden rounded-card border border-border bg-card p-3.5 shadow-sm md:p-5">
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
          {tiles.map((k) => (
            <div key={k.label} className="rounded-btn border border-border bg-background/60 px-3 py-2">
              <p className="text-[11px] font-semibold text-muted-foreground">{k.label}</p>
              <p className={cn("mt-0.5 text-[17px] font-semibold tabular-nums", k.tone)}>{k.value}</p>
              {k.sub ? <p className="text-[11px] text-muted-foreground">{k.sub}</p> : null}
            </div>
          ))}
        </div>

        {data && agingSum > 0.5 ? (
          <div className="mt-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Старение долга")}</p>
            <div className="mt-1.5 flex h-3 w-full overflow-hidden rounded-full bg-muted" role="img" aria-label={t("Старение долга")}>
              {AGING.map((a) => {
                const w = (data.aging[a.id] / agingSum) * 100;
                return w > 0 ? <span key={a.id} className={a.tone} style={{ width: `${w}%` }} title={`${a.label}: ${money(data.aging[a.id], cur)}`} /> : null;
              })}
            </div>
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-[11.5px]">
              {AGING.map((a) => (
                <span key={a.id} className="inline-flex items-center gap-1.5 text-muted-foreground">
                  <span className={cn("size-2 rounded-full", a.tone)} aria-hidden />
                  {a.label}: <span className="tabular-nums text-foreground">{money(data.aging[a.id], cur)}</span>
                </span>
              ))}
            </div>
          </div>
        ) : null}

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Клиент или продавец")} aria-label={t("Поиск по дебиторке")} className={cn(input, "max-w-xs")} />
          {data?.sellers.length ? (
            <select
              value={seller}
              onChange={(e) => setSeller(e.target.value)}
              aria-label={t("Продавец")}
              className="h-9 cursor-pointer rounded-btn border border-border bg-background px-2.5 text-[13px] text-foreground outline-none transition focus:border-primary"
            >
              <option value="">{t("Все продавцы")}</option>
              {data.sellers.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          ) : null}
          <label className="inline-flex cursor-pointer items-center gap-1.5 text-[12px] text-muted-foreground">
            <input type="checkbox" checked={onlyOverdue} onChange={(e) => setOnlyOverdue(e.target.checked)} />
            {t("Только просроченные")}
          </label>
          {canAll ? (
            <label className="inline-flex cursor-pointer items-center gap-1.5 text-[12px] text-muted-foreground">
              <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} />
              {t("Только мои клиенты")}
            </label>
          ) : (
            <span className="text-[11.5px] text-muted-foreground">{t("Показаны только ваши клиенты")}</span>
          )}
          <span className="ms-auto text-[12px] text-muted-foreground">{t("{n} клиентов", { n: items.length })}</span>
        </div>
        {error ? <p className="mt-2 text-[12px] text-red-600">{error}</p> : null}

        <div className="mt-2 overflow-x-auto rounded-btn border border-border">
          <table className="w-full text-[12px]">
            <thead className="bg-background/60 text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-start font-semibold">{t("Клиент")}</th>
                <th className="px-3 py-2 text-start font-semibold">{t("Продавец")}</th>
                <th className="px-3 py-2 text-end font-semibold">{t("Счетов")}</th>
                <th className="px-3 py-2 text-end font-semibold">{t("Долг")}</th>
                <th className="px-3 py-2 text-end font-semibold">{t("Просрочено")}</th>
                <th className="px-3 py-2 text-start font-semibold">{t("Самый старый срок")}</th>
                <th className="px-3 py-2 text-start font-semibold">{t("Последняя оплата")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {items.map((x) => (
                <Row key={x.booksId} x={x} open={open === x.booksId} onToggle={() => setOpen(open === x.booksId ? null : x.booksId)} onCard={() => openCard(x)} />
              ))}
              {items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-[12.5px] text-muted-foreground">
                    {data ? t("Долгов нет.") : t("Загрузка…")}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">
          {t("Долг — остаток по счетам Zoho Books без черновиков и аннулированных; просрочка — по сроку оплаты счёта. Клиенты, не связанные с компанией или контактом Hub, открываются в Books.")}
        </p>
      </div>
    </Band>
  );
}

function Row({ x, open, onToggle, onCard }: { x: Receivable; open: boolean; onToggle: () => void; onCard: () => void }) {
  const [docs, setDocs] = useState<CustomerFinance | null>(null);
  useEffect(() => {
    if (!open || docs) return;
    financeApi.customer({ booksId: x.booksId }).then(setDocs, () => undefined);
  }, [open, docs, x.booksId]);
  const linked = Boolean(x.companyId || x.contactId);
  return (
    <>
      <tr className={cn("cursor-pointer transition hover:bg-muted/50", open && "bg-muted/40")} onClick={onToggle} aria-expanded={open}>
        <td className="px-3 py-2">
          <span className="flex items-center gap-1.5">
            <i className={cn("text-[11px] text-muted-foreground transition", open ? "ri-arrow-down-s-line" : "ri-arrow-right-s-line")} aria-hidden />
            {linked ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onCard();
                }}
                className="cursor-pointer text-start font-semibold text-foreground hover:text-primary hover:underline"
              >
                {x.name}
              </button>
            ) : (
              <a href={x.booksUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="font-semibold text-foreground hover:text-primary hover:underline">
                {x.name}
              </a>
            )}
          </span>
        </td>
        <td className="px-3 py-2 text-muted-foreground">{x.salesperson || "—"}</td>
        <td className="px-3 py-2 text-end tabular-nums">{x.openInvoices}</td>
        <td className="px-3 py-2 text-end tabular-nums font-semibold">{money(x.receivable, x.currency)}</td>
        <td className={cn("px-3 py-2 text-end tabular-nums", x.overdue > 0.5 ? "font-semibold text-red-600" : "text-muted-foreground")}>
          {x.overdue > 0.5 ? `${money(x.overdue, x.currency)} · ${days(x.overdueDays)}` : "—"}
        </td>
        <td className="px-3 py-2 tabular-nums text-muted-foreground">{day(x.oldestDueAt)}</td>
        <td className="px-3 py-2 tabular-nums text-muted-foreground">{day(x.lastPaymentAt)}</td>
      </tr>
      {open ? (
        <tr>
          <td colSpan={7} className="bg-background/40 px-3 py-2">
            {docs ? (
              <>
                <p className="text-[11.5px] text-muted-foreground">
                  {t("Выручка (счета)")}: <span className="tabular-nums text-foreground">{money(docs.summary.revenue, docs.summary.currency)}</span>
                  {docs.summary.avgPayDays !== null ? ` · ${t("платят в среднем за {d}", { d: days(docs.summary.avgPayDays) })}` : ""}
                  {" · "}
                  <a href={x.booksUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                    {t("открыть в Books")}
                  </a>
                </p>
                <ul className="mt-1.5 flex flex-col divide-y divide-border/60 rounded-btn border border-border bg-card">
                  {[...docs.docs.filter((o) => o.kind === "invoice" && o.balance > 0.5), ...docs.docs.filter((o) => !(o.kind === "invoice" && o.balance > 0.5))].slice(0, 12).map((o) => (
                    <DocRow key={o.id} o={o} />
                  ))}
                </ul>
              </>
            ) : (
              <p className="text-[12px] text-muted-foreground">{t("Загрузка…")}</p>
            )}
          </td>
        </tr>
      ) : null}
    </>
  );
}

export default FinanceDemo;
